import React from 'react';
import { Activity, LayoutGrid, Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const POINTS = [
  { icon: Search, title: 'Scout every player', text: 'NRL stats, reports and your watch list in one place' },
  { icon: LayoutGrid, title: 'Plan your list', text: 'See who is contracted, where you are short, and try signings first' },
  { icon: Activity, title: 'Prepare for game day', text: 'Previews before each match and reviews after it' },
];

/** Faint field markings behind the brand panel */
const FieldLines: React.FC = () => (
  <svg className="absolute inset-0 h-full w-full text-white/[0.06]" aria-hidden="true" preserveAspectRatio="none" viewBox="0 0 100 100">
    {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(y => (
      <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="currentColor" strokeWidth={y === 50 ? 0.6 : 0.3} vectorEffect="non-scaling-stroke" />
    ))}
  </svg>
);

const Mark: React.FC<{ size: string }> = ({ size }) => (
  <div className={`${size} flex-shrink-0 rounded-xl bg-rugby-gold-500 flex items-center justify-center shadow-lg shadow-black/20`}>
    <span className="font-bold text-rugby-primary-900">RL</span>
  </div>
);

export const LoginPage: React.FC = () => {
  const { state, loginWithAuth0 } = useAuth();

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rugby-primary-600" aria-label="Loading"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-neutral-50 font-quicksand">
      {/* Brand panel: a header on phones, the left half on wide screens */}
      <div className="relative overflow-hidden bg-gradient-to-br from-rugby-primary-500 via-rugby-primary-600 to-rugby-primary-800 text-white lg:w-1/2">
        <FieldLines />
        <div className="relative px-6 py-8 sm:px-10 lg:px-16 lg:py-16 lg:h-full lg:flex lg:flex-col lg:justify-between">
          <div className="flex items-center gap-3">
            <Mark size="h-10 w-10 text-base" />
            <span className="text-xl font-bold tracking-tight">RL Scouting</span>
          </div>

          <div className="hidden lg:block max-w-md">
            <h1 className="text-4xl font-bold leading-tight">Know your list. Find the next signing.</h1>
            <p className="mt-4 text-rugby-primary-100">
              Scouting, list planning and match analysis for rugby league clubs.
            </p>
            <ul className="mt-10 space-y-6">
              {POINTS.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex gap-4">
                  <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-white/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-rugby-gold-400" />
                  </div>
                  <div>
                    <p className="font-semibold">{title}</p>
                    <p className="text-sm text-rugby-primary-100">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="hidden lg:block text-xs text-rugby-primary-200">Match data from NRL.com</p>
        </div>
      </div>

      {/* Log in */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold text-neutral-900">Welcome back</h2>
          <p className="mt-1 text-sm text-neutral-600 lg:hidden">Scouting, list planning and match analysis for rugby league clubs.</p>
          <p className="mt-1 text-sm text-neutral-600 hidden lg:block">Log in to your club's account.</p>

          <button
            onClick={loginWithAuth0}
            className="mt-8 w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-rugby-primary-600 shadow-sm hover:bg-rugby-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rugby-primary-500 transition-colors"
          >
            Log in
          </button>

          {state.error && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-4" role="alert">
              <p className="text-sm text-red-800">{state.error}</p>
            </div>
          )}

          <div className="mt-8 border-t border-neutral-200 pt-6">
            <p className="text-sm text-neutral-600">
              Accounts are set up by your club's administrator. If you don't have one, ask them to add you.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
